/* A-Champs 2026 · media, tabs and step tracking. No dependencies. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Play / stop helpers.
     The thumbnail is a real <img> under the video (see ac-media.liquid). The video is transparent until it is
     actually playing, and goes transparent again (and resets) when it stops, so the thumbnail shows whenever
     the video is not playing. */
  const showVideo = (v) => { v.style.opacity = '1'; };
  const hideVideo = (v) => { v.style.opacity = '0'; };
  const playV = (v) => {
    if (v.preload === 'none') v.preload = 'auto';
    v.dataset.acPlayed = '1';
    const p = v.play(); if (p && p.catch) p.catch(() => {});
  };
  const stopV = (v) => {
    v.pause();
    hideVideo(v);
    if (v.dataset.acPlayed) { delete v.dataset.acPlayed; v.preload = 'none'; v.load(); }
  };

  /* Videos: play only while visible, pause when off screen. Reduced motion = poster + controls. */
  const videos = () => document.querySelectorAll('.ac-media--video video');
  const setup = (v) => {
    if (v.dataset.acReady) return;
    v.dataset.acReady = '1';
    v.muted = true; v.loop = true; v.playsInline = true;
    v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
    v.removeAttribute('autoplay');
    v.addEventListener('playing', () => showVideo(v));
    if (reduce) { v.controls = true; showVideo(v); return; }
    if (inGroup(v)) { setupGroupVideo(v); return; }
    io.observe(v);
  };
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting && v.offsetParent !== null) { playV(v); }
      else if (!v.paused) { stopV(v); }
    });
  }, { threshold: 0.35 });

  /* Tabs: built from .ac-tab children, works without JS as a stacked list. */
  const initTabs = (root) => {
    if (root.classList.contains('is-ready')) return;
    const nav = root.querySelector('.ac-tabs__nav');
    const tabs = [...root.querySelectorAll('.ac-tab')];
    if (!nav || tabs.length < 2) return;
    tabs.forEach((tab, i) => {
      const id = `ac-tab-${Math.random().toString(36).slice(2, 8)}`;
      tab.id = id; tab.setAttribute('role', 'tabpanel');
      const b = document.createElement('button');
      b.type = 'button'; b.setAttribute('role', 'tab'); b.setAttribute('aria-controls', id);
      b.textContent = tab.dataset.title || `Tab ${i + 1}`;
      b.addEventListener('click', () => select(i));
      b.addEventListener('keydown', (ev) => {
        if (ev.key === 'ArrowRight') { select((i + 1) % tabs.length); nav.children[(i + 1) % tabs.length].focus(); }
        if (ev.key === 'ArrowLeft') { const j = (i - 1 + tabs.length) % tabs.length; select(j); nav.children[j].focus(); }
      });
      nav.appendChild(b);
    });
    const select = (n) => {
      tabs.forEach((t, i) => { t.hidden = i !== n; nav.children[i].setAttribute('aria-selected', String(i === n)); nav.children[i].tabIndex = i === n ? 0 : -1; });
    };
    root.classList.add('is-ready');
    select(0);
  };

  /* Steps (workflow sections)
     - Scrolling highlights the step nearest the viewport centre, until the visitor clicks a step;
       after that the visitor is in control and scrolling no longer changes the selection.
     - Every step is a keyboard/click target (Enter / Space / arrow keys).
     - If steps carry their own visual, the section visual swaps to it (desktop), or each visual sits
       inside its step (mobile). Without JS the section's own visual is shown. */
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const initSteps = (list) => {
    if (list.dataset.acSteps) return;
    const steps = [...list.querySelectorAll(':scope > .ac-step')];
    if (steps.length < 2) return;
    list.dataset.acSteps = '1';
    const sec = list.closest('.ac-sec, .ac');
    const aside = sec && sec.querySelector('.ac-media-block');
    const hasMedia = steps.some((st) => st.querySelector('template.ac-step__media'));
    const original = aside ? aside.innerHTML : '';
    let manual = false; let current = -1;

    const showMedia = (i) => {
      if (!aside || !hasMedia || mqMobile.matches) return;
      const tpl = steps[i].querySelector('template.ac-step__media');
      const html = tpl ? tpl.innerHTML : original;
      if (aside.dataset.acShown === String(i)) return;
      aside.dataset.acShown = String(i);
      aside.classList.add('is-swapping');
      setTimeout(() => {
        aside.innerHTML = html;
        aside.querySelectorAll('.ac-media--video video').forEach(setup);
        aside.classList.remove('is-swapping');
      }, reduce ? 0 : 180);
    };
    const select = (i, byUser) => {
      if (byUser) { manual = true; list.classList.add('is-manual'); }
      if (i === current) return;
      current = i;
      steps.forEach((st, k) => { st.classList.toggle('is-active', k === i); st.setAttribute('aria-selected', String(k === i)); st.tabIndex = k === i ? 0 : -1; });
      showMedia(i);
    };
    list.setAttribute('role', 'tablist');
    list.classList.add('is-tracking');
    steps.forEach((st, i) => {
      st.setAttribute('role', 'tab');
      st.addEventListener('click', () => select(i, true));
      st.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); select(i, true); }
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') { ev.preventDefault(); const j = (i + 1) % steps.length; select(j, true); steps[j].focus(); }
        if (ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') { ev.preventDefault(); const j = (i - 1 + steps.length) % steps.length; select(j, true); steps[j].focus(); }
      });
    });
    /* mobile: each step shows its own visual inline */
    const inline = () => {
      steps.forEach((st) => {
        const tpl = st.querySelector('template.ac-step__media');
        let box = st.querySelector(':scope > .ac-step__visual');
        if (mqMobile.matches && tpl && !box) {
          box = document.createElement('div'); box.className = 'ac-step__visual'; box.innerHTML = tpl.innerHTML;
          st.appendChild(box); box.querySelectorAll('.ac-media--video video').forEach(setup);
        } else if (!mqMobile.matches && box) { box.remove(); }
      });
      if (aside) aside.classList.toggle('ac-hide-when-inline', mqMobile.matches && steps.every((st) => st.querySelector('template.ac-step__media')));
    };
    if (hasMedia) { inline(); mqMobile.addEventListener('change', () => { inline(); if (current >= 0) { delete aside.dataset.acShown; showMedia(current); } }); }
    select(0, false);
    const so = new IntersectionObserver((entries) => {
      if (manual) return;
      entries.forEach((e) => { if (e.isIntersecting) select(steps.indexOf(e.target), false); });
    }, { rootMargin: '-45% 0px -45% 0px' });
    steps.forEach((st) => so.observe(st));
  };

  /* Video card grids: several videos side by side never play at once.
     Desktop: a card plays while hovered or focused. Touch: only the card crossing the centre of the screen plays. */
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const inGroup = (v) => {
    const grid = v.closest('.ac-grid, .ac-eco, .ac-quotes');
    return grid && grid.querySelectorAll('.ac-media--video').length > 1 ? grid : null;  /* count media slots, not files: a slot may hold a desktop + a mobile video */
  };
  const centreIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => { const v = e.target; if (e.isIntersecting) playV(v); else if (!v.paused) stopV(v); });
  }, { rootMargin: '-38% 0px -38% 0px' });
  const setupGroupVideo = (v) => {
    const card = v.closest('.ac-card, .ac-flag, .ac-prod, .ac-panel') || v.parentElement;
    if (hoverCapable) {
      const play = () => { if (v.offsetParent === null) return; playV(v); };
      const stop = () => stopV(v);
      card.addEventListener('mouseenter', play); card.addEventListener('mouseleave', stop);
      card.addEventListener('focusin', play); card.addEventListener('focusout', stop);
    } else { centreIO.observe(v); }
  };

  /* Resolution panel: draw the signal beam when it enters the viewport. */
  document.documentElement.classList.add('ac-js');
  const ro = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); ro.unobserve(e.target); } });
  }, { threshold: 0.4 });

  /* Sticky buy bar: show after the first screen, hide when the footer is in view. */
  const initSticky = (bar) => {
    if (bar.dataset.acReady) return; bar.dataset.acReady = '1';
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    let footerVisible = false;
    if (footer) new IntersectionObserver((e) => { footerVisible = e[0].isIntersecting; update(); }).observe(footer);
    const update = () => {
      const show = window.scrollY > window.innerHeight * 0.9 && !footerVisible;
      bar.classList.toggle('is-visible', show);
      bar.hidden = false;
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelectorAll('a').forEach((a) => { a.tabIndex = show ? 0 : -1; });
    };
    window.addEventListener('scroll', update, { passive: true }); update();
  };

  /* "#buy" links (sticky bar on product pages): bring the buy box into view and focus Add to Cart */
  document.addEventListener('click', (ev) => {
    const a = ev.target.closest && ev.target.closest('a[href="#buy"]');
    if (!a) return;
    const box = document.querySelector('.product-info, product-info, [id$="__main"]');
    if (!box) return;
    ev.preventDefault();
    box.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    const btn = box.querySelector('button[type="submit"], .product-info__buy-buttons button');
    if (btn) setTimeout(() => btn.focus({ preventScroll: true }), reduce ? 0 : 600);
  });

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ac-sticky]').forEach(initSticky);
    scope.querySelectorAll('[data-ac-resolve]').forEach((el) => ro.observe(el));
    videos().forEach(setup);
    scope.querySelectorAll('[data-ac-tabs]').forEach(initTabs);
    scope.querySelectorAll('.ac-steps').forEach(initSteps);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();